import { useEffect, useId, useRef, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { IconBrandTeams } from "@tabler/icons-react";
import { useTranslation } from "@/i18n";
import { MS_TEAMS_INTEGRATION_FLAG } from "@/lib/launchdarkly/flags";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/panel";
import { useConfirmDialog } from "@/components/primitives/confirm-dialog";
import { TeamsLinkRow } from "@/components/profile-settings/notifications/teams-link-row";
import { useStore } from "@/stores/store-context";
import { isConnected } from "@/lib/integrations/msteams-link";
import { useUserTeamsLinks, useUserTeamsLinkWriters } from "@/hooks/use-user-teams-links";
import { teamsErrorCode } from "@/lib/integrations/msteams-errors";
import { TEAMS_SIGNIN_ERROR_CODES } from "@/types/msteams";
import type { TTeamsSignInErrorCode } from "@/types/msteams";

/** Query params the Microsoft sign-in callback comes back with — the same two the
 *  workspace card reads, because there is one callback endpoint for both purposes
 *  (`views_connection.CONNECTED_PARAM` / `ERROR_PARAM`). */
const CONNECTED_PARAM = "msteams_connected";
const ERROR_PARAM = "msteams_error";

/** Set by the "Link my Cognota account" button on the bot's unmatched card, so the
 *  person arriving from Teams lands looking at this block rather than at the top of a
 *  page of notification switches. Carries no state and grants nothing. */
const LINK_PARAM = "msteams_link";

function isSignInErrorCode(value: string): value is TTeamsSignInErrorCode {
  return (TEAMS_SIGNIN_ERROR_CODES as readonly string[]).includes(value);
}

/** Microsoft Teams, as one block above the notification switches (plan §5a FR5–FR7,
 *  NG-1134, epic NG-886).
 *
 *  **It sits with the notification preferences because that is what it is** — a
 *  delivery channel for the same events the switches below it turn on and off, not a
 *  connected app in the OAuth sense. The Connected apps panel holds grants that let a
 *  third party act *as* you; this grants Cognota nothing and only says where to send
 *  your own notifications.
 *
 *  **Rendered only when there is something to say.** Two gates, and both mean "show
 *  nothing" rather than "show an empty state": the `ms-teams-integration` flag is off
 *  in every real environment today, and a person whose workspaces have never enabled
 *  Teams has no action to take — offering them Connect would send them to Microsoft to
 *  achieve nothing, and the flow would come back `no_enabled_workspace`.
 *
 *  **One row, for the workspace they are looking at** (NG-1622). It used to list
 *  every workspace they hold a link on, which asked somebody opening their
 *  notification settings in one workspace to reason about workspaces they were not
 *  thinking about. The rest are accounted for in a caption rather than dropped, so
 *  the block never quietly loses a channel that is still messaging them.
 *
 *  **A workspace with no Teams connection is absent from the payload**, so the
 *  current one usually has no row of its own — a person in eight workspaces may have
 *  Teams in two. That case renders the row as *not set up here*, never an empty
 *  block: the block is the only place to turn Teams off, and hiding it from the six
 *  would strand somebody who is still receiving cards in the other two.
 *
 *  **The actions are block-level, not per row, because the underlying operations
 *  are.** One Microsoft sign-in proves one identity and binds it to *every* workspace
 *  of yours on that tenant at once; `unlink` is global for the person by design
 *  (FR7 — somebody switching this off does not expect a second workspace to keep
 *  messaging them); and the install steps describe one app, the same one, wherever
 *  they are told. A Disconnect button drawn on the row would unlink all of them,
 *  which is the kind of lie a per-row control tells. Showing a single row is a fresh
 *  chance to tell it — one row beside one pair of buttons reads as that row's
 *  buttons — so the Connect action carries a line saying one sign-in covers every
 *  workspace, and Turn off names the same scope in its confirm. The row stays
 *  informational: which workspace, what state, and the one line that says what would
 *  fix it. Per-workspace unlink is explicitly out of scope, as it was for NG-1134.
 *
 *  **Installing is explained, not linked** (NG-1186). There used to be an "Install
 *  Cognota in Teams" button on `https://teams.microsoft.com/l/app/<bot app id>`; that
 *  id is the manifest id, which Teams resolves only for a *sideloaded* app. Every
 *  customer installs from their org catalog, where the link needs the tenant's own
 *  catalog id — readable only with `AppCatalog.Read.All` and admin consent we do not
 *  ask for — so the button answered "This app cannot be found." for everybody who
 *  pressed it. Four words of instruction always work; a link that never resolves
 *  never does.
 *
 *  The landing effect synchronises with an external navigation — Microsoft's redirect
 *  back — rather than fetching store-owned data, which is why it is a `useEffect`
 *  (the server-data rule's exception), the same shape `teams-connection-panel.tsx` uses.
 */
export const TeamsLinkBlock = observer(function TeamsLinkBlock() {
  const { t } = useTranslation();
  const { featureFlags, workspace } = useStore();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const headingId = useId();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [scrollRequested, setScrollRequested] = useState(false);

  // Mirrors how `routes/workspace/settings/integration-library.tsx` reads it. An
  // unresolved flag reads false here and the block renders nothing, which is the
  // right way round for an additive block: it appears when the flag resolves on,
  // rather than flashing a skeleton on a page it may not belong to at all.
  const enabled = featureFlags.isEnabled(MS_TEAMS_INTEGRATION_FLAG, false);
  const userTeamsLink = useUserTeamsLinks({ enabled });
  const writers = useUserTeamsLinkWriters();
  const refreshLinks = userTeamsLink.refresh;

  // The block reads the links fresh on every mount, where the bell and the
  // notification column only read them if nobody has (NG-2813).
  useEffect(() => {
    if (!enabled) return;
    void refreshLinks().catch((error: unknown) => {
      // A failed read renders nothing rather than an error banner: this is one
      // optional block on a page of working switches, and a channel we cannot
      // describe must not be described wrongly.
      console.warn("[msteams] failed to read my Teams links", { error });
    });
  }, [enabled]);

  // Redirect-only effect: turns the callback's params into a toast, re-reads the rows
  // the callback just wrote, and strips the params so a reload cannot re-toast.
  useEffect(() => {
    if (!enabled) return;
    const params = new URLSearchParams(window.location.search);
    const connected = params.get(CONNECTED_PARAM);
    const errorCode = params.get(ERROR_PARAM);
    const cameFromTeams = params.get(LINK_PARAM);
    if (!connected && !errorCode && !cameFromTeams) return;

    if (connected) {
      // The callback wrote the link: this redirect is the only place it shows.
      track("teams_link_connected", { source: "page" });
      toast.success(t("profile_settings.msteams.redirect_connected"));
      void refreshLinks().catch((error: unknown) => {
        // The block keeps showing what it had rather than blanking.
        console.warn("[msteams] failed to re-read my Teams links after sign-in", { error });
      });
    } else if (errorCode) {
      // Matched against the closed set the callback can emit rather than trusted as a
      // key fragment: the value sits in a URL anybody can type, and an unrecognised
      // one has to reach the reader as a plain failure, not a missing-key string.
      track("teams_link_signin_failed", {
        reason: isSignInErrorCode(errorCode) ? errorCode : "unknown",
        source: "page",
      });
      toast.error(
        isSignInErrorCode(errorCode)
          ? t(`profile_settings.msteams.redirect_error_${errorCode}`)
          : t("profile_settings.msteams.redirect_error_unknown"),
      );
    }

    if (cameFromTeams) setScrollRequested(true);

    params.delete(CONNECTED_PARAM);
    params.delete(ERROR_PARAM);
    params.delete(LINK_PARAM);
    const nextQuery = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`,
    );
  }, [enabled]);

  const visible = enabled && userTeamsLink.hasConnectedWorkspaces;

  // Deferred to its own effect rather than done in the one above, because the block
  // does not exist until the read resolves — scrolling to a ref that is still null is
  // how "the button from Teams does nothing" happens.
  useEffect(() => {
    if (!scrollRequested || !visible) return;
    sectionRef.current?.scrollIntoView({ block: "center" });
    setScrollRequested(false);
  }, [scrollRequested, visible]);

  const handleConnect = async () => {
    try {
      const url = await writers.startSignIn(window.location.pathname);
      window.location.assign(url);
    } catch (error) {
      // 503 `signin_not_configured` is the one failure with a different recourse:
      // nobody in the workspace can fix it, so it must not read as "try again".
      toast.error(
        teamsErrorCode(error) === "signin_not_configured"
          ? t("profile_settings.msteams.connect_not_configured")
          : t("profile_settings.msteams.connect_error"),
      );
    }
  };

  const handleDisconnect = async () => {
    const confirmed = await confirm({
      title: t("profile_settings.msteams.disconnect_confirm_title"),
      description: t("profile_settings.msteams.disconnect_confirm_description"),
      destructive: true,
      confirmLabel: t("profile_settings.msteams.disconnect_action"),
    });
    if (!confirmed) return;
    try {
      await writers.unlink();
      toast.success(t("profile_settings.msteams.disconnect_success"));
    } catch {
      // The writer logged it and applied nothing, so the rows still show the truth.
      toast.error(t("profile_settings.msteams.disconnect_error"));
    }
  };

  if (!visible) return null;

  const rows = userTeamsLink.workspaces;
  const pending = writers.pendingAction;

  // What the block *displays* is scoped to one workspace; what its buttons *do* is
  // not. `currentWorkspace` reads the router slug against the loaded workspace list,
  // the same way `routes/workspace/settings/integration-library.tsx` does, and rows
  // are matched on the workspace id rather than the slug because the id is the thing
  // neither side can rename.
  const currentWorkspace = workspace.currentWorkspace;
  const currentRow = rows.find((row) => row.workspace.id === currentWorkspace?.id) ?? null;
  // The payload's own name in preference to the store's: when both describe the same
  // workspace they agree, and when a rename has not reached one of them the row
  // should read as the channel that would carry it.
  const currentWorkspaceName = currentRow?.workspace.name ?? currentWorkspace?.name ?? null;
  // The store's word, not a second copy of it: "connected" is defined once, in
  // `isConnected`, and the row's Connected badge reads the same fact through the same
  // definition. A row the person unlinked, or never linked, is not one they are
  // "also connected in".
  const otherConnectedCount = userTeamsLink.otherConnectedWorkspaces.length;

  // The three predicates below deliberately read **every** row, not the one on
  // screen, because every action they gate is global. Narrowing them to the visible
  // workspace would take Turn off away from exactly the person who needs it — the
  // one whose only link is somewhere else and who came here to stop the cards.
  //
  // Offer Connect only where some row has **no matched link** — a Microsoft sign-in
  // is what produces one, so a person whose every row is already matched has nothing
  // to prove and would be sent to Microsoft to be told so. `!receiving` was too
  // broad: it kept the button up for somebody whose only gap is the missing Teams
  // app, next to a row saying their account is connected.
  //
  // A row whose connection is paused still counts, because signing in records the
  // link regardless of `is_enabled` — the person is then ready the moment their admin
  // resumes the channel.
  const canConnect = rows.some((row) => !isConnected(row));
  // Only worth showing where the identity is already proven and the app is missing —
  // the one state the person can fix themselves without going near Microsoft again.
  const needsInstall = rows.some((row) => isConnected(row) && !row.link?.installed);
  // A person with no row anywhere has nothing to turn off, and an already-unlinked one
  // has nothing left to turn off either — unlink is idempotent, but a button that does
  // nothing visible is noise.
  const canDisconnect = rows.some((row) => row.link !== null && row.link.state !== "unlinked");

  return (
    <Panel ref={sectionRef} className="space-y-4" aria-labelledby={headingId}>
      <div className="flex items-start gap-3">
        <IconBrandTeams className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <h3 id={headingId} className="text-sm font-medium text-foreground">
            {t("profile_settings.msteams.title")}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("profile_settings.msteams.description")}
          </p>
        </div>
      </div>

      {/* Rendered whenever we know which workspace this is, with or without a row of
          its own — `currentRow` being null is the ordinary case, not an error. Only a
          workspace we cannot name at all falls through to nothing, because neither
          "this workspace" nor "the others" has a subject then; the actions stay
          either way. */}
      {currentWorkspaceName ? (
        <TeamsLinkRow workspaceName={currentWorkspaceName} row={currentRow} />
      ) : null}

      {/* The workspaces this one is not. Absent at zero rather than said as "no
          others": somebody with a single workspace is not missing anything. */}
      {otherConnectedCount > 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("profile_settings.msteams.other_workspaces", { count: otherConnectedCount })}
        </p>
      ) : null}

      {/* Where the deep-link button used to be, and deliberately text rather than an
          action: the app is added inside Teams, in a client we cannot navigate. One
          line for the whole block rather than per row, because the app is installed
          once for the person and every row that is missing it is missing the same
          one. */}
      {needsInstall ? (
        <p className="text-sm text-muted-foreground">
          {t("profile_settings.msteams.install_steps")}
        </p>
      ) : null}

      {/* Says out loud what the Connect button would otherwise have to be guessed to
          mean now that one row sits above it: the sign-in is not this workspace's.
          Only where Connect is on offer — with nothing left to sign in for, a line
          about the reach of a sign-in is noise. */}
      {canConnect ? (
        <p className="text-sm text-muted-foreground">
          {t("profile_settings.msteams.connect_scope_hint")}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {canConnect ? (
          <Button onClick={handleConnect} disabled={pending === "signin"}>
            {pending === "signin"
              ? t("profile_settings.msteams.connect_pending")
              : t("profile_settings.msteams.connect_action")}
          </Button>
        ) : null}

        {canDisconnect ? (
          <Button variant="outline" onClick={handleDisconnect} disabled={pending === "unlink"}>
            {pending === "unlink"
              ? t("profile_settings.msteams.disconnect_pending")
              : t("profile_settings.msteams.disconnect_action")}
          </Button>
        ) : null}
      </div>

      {ConfirmDialog}
    </Panel>
  );
});
